import { useEffect, useState, useRef } from "react";
import { FileArrowDown, UploadSimple, Trash, Files, LinkSimple, LinkBreak } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";

export const REPORT_DOC_SLOTS = [
  { type: "chartered_engineer", label: "Chartered Engineer Certificate" },
  { type: "survey_report", label: "Survey Report" },
  { type: "civil_estimate", label: "Civil Estimate" },
  { type: "structural_estimate", label: "Structural Estimate" },
  { type: "income_tax_valuation", label: "Income Tax Valuation" },
  { type: "measurement_certification", label: "Measurement Certification" },
];

function Slot({ jobId, slot, docs, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const files = docs.filter((d) => d.doc_type === slot.type);

  const upload = async (target) => {
    const file = target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", slot.type);
      await api.post(`/jobs/${jobId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${slot.label} uploaded`);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (target) target.value = "";
    }
  };

  const remove = async (docId) => {
    try {
      await api.delete(`/jobs/${jobId}/documents/${docId}`);
      await onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const share = async (docId) => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/documents/${docId}/share`);
      try { await navigator.clipboard.writeText(data.url); } catch { /* clipboard may be blocked */ }
      toast.success("Public link copied — anyone with it can download (no login needed)");
      await onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unshare = async (docId) => {
    try { await api.delete(`/jobs/${jobId}/documents/${docId}/share`); toast.success("Public link revoked"); await onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid={`report-doc-slot-${slot.type}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-primary flex items-center gap-2">{slot.label}
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${files.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`report-doc-status-${slot.type}`}>{files.length ? "Received" : "Pending"}</span>
        </span>
        <label className="text-xs text-accent hover:underline cursor-pointer flex items-center gap-1 shrink-0">
          <UploadSimple size={14} /> {busy ? "Uploading…" : (files.length ? "Replace / Add" : "Upload")}
          <input ref={inputRef} type="file" className="hidden" disabled={busy} data-testid={`report-doc-upload-${slot.type}`}
            onChange={(e) => upload(e.target)}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt" />
        </label>
      </div>
      {files.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-1.5">No file uploaded yet.</p>
      ) : (
        <div className="space-y-1.5 mt-2">
          {files.map((d) => (
            <div key={d.id} className="flex items-center gap-2 p-2 rounded-md bg-background border border-border text-sm">
              <FileArrowDown size={16} className="text-primary shrink-0" />
              <a href={`${API}/jobs/${jobId}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                className="text-primary hover:underline truncate flex-1" data-testid={`report-doc-download-${d.id}`}>{d.filename}</a>
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">{Math.max(1, Math.round((d.size || 0) / 1024))} KB</span>
              <button type="button" title={d.share_token ? "Copy public link" : "Create public link (no login needed)"} className={`shrink-0 ${d.share_token ? "text-emerald-600" : "text-muted-foreground hover:text-accent"}`} onClick={() => share(d.id)} data-testid={`report-doc-share-${d.id}`}><LinkSimple size={14} weight={d.share_token ? "bold" : "regular"} /></button>
              {d.share_token && <button type="button" title="Revoke public link" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => unshare(d.id)} data-testid={`report-doc-unshare-${d.id}`}><LinkBreak size={14} /></button>}
              <button type="button" className="text-destructive shrink-0" onClick={() => remove(d.id)} data-testid={`report-doc-delete-${d.id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReportDocSlots({ jobId }) {
  const [docs, setDocs] = useState([]);

  const load = async () => {
    try { const { data } = await api.get(`/jobs/${jobId}/documents`); setDocs(Array.isArray(data) ? data : []); }
    catch { /* leave as-is */ }
  };
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId]);

  const received = REPORT_DOC_SLOTS.filter((s) => docs.some((d) => d.doc_type === s.type)).length;
  const pending = REPORT_DOC_SLOTS.length - received;

  return (
    <div className="border-t border-border pt-3 space-y-2" data-testid="report-doc-slots">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground flex items-center gap-1.5">
          <Files size={14} className="text-accent" /> Statutory Reports
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${pending ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="report-doc-summary">{received}/{REPORT_DOC_SLOTS.length} received{pending ? ` · ${pending} pending` : ""}</span>
      </div>
      <p className="text-[11px] text-muted-foreground -mt-1">Upload each supporting document, share any file with a bank via a no-login link. {pending ? "Pending items are still missing before release." : "All supporting reports received."}</p>
      <div className="grid gap-2">
        {REPORT_DOC_SLOTS.map((s) => (
          <Slot key={s.type} jobId={jobId} slot={s} docs={docs} onChanged={load} />
        ))}
      </div>
    </div>
  );
}
